/**
 * WebSocket 客户端
 * 用于通过 wss 发送 POST 请求并接收响应（二进制协议）
 */

import type { AxiosRequestConfig } from "axios";

/**
 * 请求配置类型（基于 axios 的 AxiosRequestConfig）
 */
export type ApiRequestConfig = Pick<
  AxiosRequestConfig,
  "url" | "method" | "data" | "headers"
>;

/**
 * WebSocket 消息格式（用于发送）
 */
export interface WsMessage {
  api: string;
  method: string;
  headers: Record<string, string>;
  data: unknown;
}

/**
 * 二进制响应解析类
 */
class BinaryResponse {
  buffer: Uint8Array;
  status: string;

  constructor(buffer: ArrayBuffer) {
    this.buffer = new Uint8Array(buffer);
    this.status = this.buffer[4] === 0 ? "OK" : "Failed";
  }

  getReqId(): number {
    return new DataView(this.buffer.buffer).getUint32(0);
  }

  isPush(): boolean {
    return this.getReqId() === 1;
  }

  getData(): ArrayBuffer {
    let offset = 5;
    if (this.isPush()) {
      offset += 4;
    }
    if (this.buffer.byteLength <= offset) {
      return new ArrayBuffer(0);
    }
    return this.buffer.slice(offset).buffer;
  }

  createPushAck(): ArrayBuffer {
    if (!this.isPush() || this.buffer.byteLength <= 9) {
      return new ArrayBuffer(0);
    }
    const ackBuffer = new ArrayBuffer(9);
    const view = new DataView(ackBuffer);
    view.setUint32(0, 1);
    view.setUint8(4, 0);
    view.setUint32(5, new DataView(this.buffer.buffer).getUint32(5));
    return ackBuffer;
  }

  toString(): string {
    const decoder = new TextDecoder("utf-8");
    return decoder.decode(this.getData());
  }

  toJSON(): unknown {
    try {
      return JSON.parse(this.toString());
    } catch (err) {
      throw new Error(
        "Invalid JSON response: " +
          (err instanceof Error ? err.message : String(err))
      );
    }
  }
}

interface WsClientOptions {
  url: string;
  requestTimeout?: number;
  connectTimeout?: number;
}

class WsClient {
  private ws: WebSocket | null = null;
  private url: string;
  private requestTimeout: number;
  private connectTimeout: number;
  private connected: boolean = false;
  private connecting: boolean = false;
  private requestId: number = 10; // 从 10 开始，1 保留给推送消息
  private pendingRequests: Map<
    number,
    { resolve: (value: unknown) => void; reject: (error: Error) => void }
  > = new Map();

  constructor(options: WsClientOptions) {
    this.url = options.url;
    this.requestTimeout = options.requestTimeout || 30000;
    this.connectTimeout = options.connectTimeout || 30000;
  }

  /**
   * 连接到 WebSocket 服务器
   */
  async connect(): Promise<void> {
    if (this.connected) {
      return;
    }

    if (this.connecting) {
      // 等待连接完成
      return new Promise((resolve, reject) => {
        const checkInterval = setInterval(() => {
          if (this.connected) {
            clearInterval(checkInterval);
            resolve();
          } else if (!this.connecting) {
            clearInterval(checkInterval);
            reject(new Error("Connection failed"));
          }
        }, 100);
      });
    }

    return new Promise((resolve, reject) => {
      try {
        this.connecting = true;

        const timeout = setTimeout(() => {
          this.connecting = false;
          reject(new Error("Connection timeout"));
        }, this.connectTimeout);

        this.ws = new WebSocket(this.url);

        // 设置 binaryType 为 arraybuffer 以支持二进制协议
        this.ws.binaryType = "arraybuffer";

        this.ws.onopen = () => {
          clearTimeout(timeout);
          this.connected = true;
          this.connecting = false;
          this.setupMessageHandler();
          resolve();
        };

        this.ws.onerror = () => {
          clearTimeout(timeout);
          this.connected = false;
          this.connecting = false;
          reject(new Error("WebSocket connection error"));
        };

        this.ws.onclose = () => {
          this.connected = false;
          this.connecting = false;
        };
      } catch (error) {
        this.connecting = false;
        reject(error);
      }
    });
  }

  /**
   * 设置消息处理器（二进制协议）
   */
  private setupMessageHandler(): void {
    if (!this.ws) return;

    this.ws.onmessage = (event: MessageEvent) => {
      try {
        // 二进制协议，数据应该是 ArrayBuffer
        if (!(event.data instanceof ArrayBuffer)) {
          console.error("Expected ArrayBuffer, got:", typeof event.data);
          return;
        }

        const response = new BinaryResponse(event.data);

        // 处理推送消息
        if (response.isPush()) {
          this.handlePushMessage(response);
          return;
        }

        // 处理请求响应
        const reqId = response.getReqId();
        const callback = this.pendingRequests.get(reqId);

        if (callback) {
          this.pendingRequests.delete(reqId);
          if (response.status === "OK") {
            try {
              const jsonData = response.toJSON();
              callback.resolve(jsonData);
            } catch {
              // 如果解析 JSON 失败，返回文本
              callback.resolve({ data: response.toString() });
            }
          } else {
            callback.reject(new Error(response.toString()));
          }
        } else {
          console.warn("Received message without matching requestId:", reqId);
        }
      } catch (error) {
        console.error("Failed to parse WebSocket message:", error);
      }
    };
  }

  /**
   * 处理推送消息
   */
  private handlePushMessage(response: BinaryResponse): void {
    const ack = response.createPushAck();
    if (ack.byteLength > 0 && this.ws) {
      this.ws.send(ack);
    }
    // 推送消息处理逻辑可以在这里扩展
  }

  /**
   * 创建二进制请求
   * @param data - 请求数据（字符串或 ArrayBuffer）
   * @param headers - 请求头
   * @param reqId - 请求 ID
   * @returns ArrayBuffer
   */
  private createRequest(
    data: string | ArrayBuffer,
    headers: Record<string, string>,
    reqId: number
  ): ArrayBuffer {
    const encoder = new TextEncoder();
    const bodyData =
      typeof data === "string" ? encoder.encode(data) : new Uint8Array(data);

    const headerPairs = Object.entries(headers);
    const headerSize = headerPairs.reduce((size, [key, value]) => {
      return size + 1 + key.length + 1 + value.length;
    }, 0);

    const buffer = new ArrayBuffer(4 + headerSize + 1 + bodyData.byteLength);
    const view = new DataView(buffer);
    view.setUint32(0, reqId);

    let offset = 4;
    const uint8View = new Uint8Array(buffer);

    // 写入 headers
    for (const [key, value] of headerPairs) {
      const keyBytes = encoder.encode(key);
      const valueBytes = encoder.encode(value);

      uint8View[offset++] = keyBytes.length;
      uint8View.set(keyBytes, offset);
      offset += keyBytes.length;

      uint8View[offset++] = valueBytes.length;
      uint8View.set(valueBytes, offset);
      offset += valueBytes.length;
    }

    // 写入 header 结束标志
    uint8View[offset++] = 0;

    // 写入 body 数据
    uint8View.set(bodyData, offset);

    return buffer;
  }

  /**
   * 发送请求消息（二进制协议）
   * @param message - 已构建好的请求消息
   * @returns Promise<[response, error]>
   */
  async send(
    message: Omit<WsMessage, "requestId">
  ): Promise<[unknown, Error | null]> {
    try {
      // 确保已连接
      if (!this.connected) {
        await this.connect();
      }

      // 等待连接完成
      while (!this.connected && this.connecting) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      if (!this.connected || !this.ws) {
        throw new Error("WebSocket is not connected");
      }

      const requestId = this.requestId++;

      // 构建请求体（JSON 字符串）
      const requestBody = JSON.stringify({
        token: localStorage.getItem("token") || "",
        data: message.data,
      });

      // 构建 headers（包含 api 和 method）
      const headers: Record<string, string> = {
        ...message.headers,
        api: message.api,
        "content-type": "application/json",
      };

      // 创建二进制请求
      const binaryRequest = this.createRequest(requestBody, headers, requestId);

      // 发送请求
      return new Promise(resolve => {
        const timeout = setTimeout(() => {
          this.pendingRequests.delete(requestId);
          resolve([null, new Error("Request timeout")]);
        }, this.requestTimeout);

        this.pendingRequests.set(requestId, {
          resolve: (response: unknown) => {
            clearTimeout(timeout);
            resolve([response, null]);
          },
          reject: (error: Error) => {
            clearTimeout(timeout);
            resolve([null, error]);
          },
        });

        this.ws!.send(binaryRequest);
      });
    } catch (error) {
      return [
        null,
        error instanceof Error ? error : new Error("Unknown error"),
      ];
    }
  }

  /**
   * 关闭连接
   */
  close(): void {
    if (this.ws) {
      this.connected = false;
      this.ws.close();
      this.ws = null;
      this.pendingRequests.clear();
    }
  }

  /**
   * 检查是否已连接
   */
  isConnected(): boolean {
    return this.connected;
  }
}

export { WsClient };
